// src/App.js

import React, { useState, useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import AppTheme from "./shared-theme/AppTheme";
import './i18n';
import ErrorBoundary from "./components/ErrorBoundary";
import WhatsAppFab from "./components/WhatsAppFab";
import ScrollToTop from "./components/ScrollToTop";
import RouteVeil from "./components/RouteVeil";

const MarketingPage = lazy(() => import("./pages/MarketingPage"));
const ProductListing = lazy(() => import("./components/ProductListing"));
const ProductPage = lazy(() => import("./components/ProductPage"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./components/TermsAndConditions"));
const RefundPolicy = lazy(() => import("./components/RefundPolicy"));
const Lookbook = lazy(() => import("./components/Lookbook"));
const CustomOrders = lazy(() => import("./components/CustomOrders"));
const LinenLanding = lazy(() => import("./components/LinenLanding"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const FinanceOverview = lazy(() => import("./components/admin/FinanceOverview"));
const SalesSection = lazy(() => import("./components/admin/sections/SalesSection"));
const ExpensesSection = lazy(() => import("./components/admin/sections/ExpensesSection"));
const StallsSection = lazy(() => import("./components/admin/sections/StallsSection"));
const RefundsSection = lazy(() => import("./components/admin/sections/RefundsSection"));
const ProductCostsSection = lazy(() => import("./components/admin/sections/ProductCostsSection"));
const StockPage = lazy(() => import("./components/admin/StockPage"));

function RouteFallback() {
  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CircularProgress sx={{ color: '#303e70' }} />
    </Box>
  );
}

function App() {
  const [cart, setCart] = useState(() => {
    try {
      const stored = localStorage.getItem("cart");
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      localStorage.removeItem("cart");
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (item) => {
    setCart((prev) => {
      const exists = prev.find(
        (p) => p.id === item.id && p.size === item.size
      );
      if (exists) {
        return prev.map((p) =>
          p.id === item.id && p.size === item.size
            ? { ...p, quantity: p.quantity + 1 }
            : p
        );
      }
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const removeFromCart = (id, size) =>
    setCart((prev) => prev.filter((p) => !(p.id === id && p.size === size)));

  // Bump a line item's quantity by `delta` (±1 from the cart stepper).
  // Dropping to zero removes the line entirely.
  const updateQuantity = (id, size, delta) =>
    setCart((prev) =>
      prev
        .map((p) =>
          p.id === id && p.size === size
            ? { ...p, quantity: p.quantity + delta }
            : p
        )
        .filter((p) => p.quantity > 0)
    );

  const clearCart = () => setCart([]);

  return (
    <ErrorBoundary>
      <AppTheme>
        <Router>
          <ScrollToTop />
          <RouteVeil />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/lookbook" element={<Lookbook />} />
              <Route path="/custom-orders" element={<CustomOrders />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<FinanceOverview />} />
                <Route path="sales" element={<SalesSection />} />
                <Route path="expenses" element={<ExpensesSection />} />
                <Route path="stalls" element={<StallsSection />} />
                <Route path="refunds" element={<RefundsSection />} />
                <Route path="product-costs" element={<ProductCostsSection />} />
                <Route path="stock" element={<StockPage />} />
              </Route>

              <Route
                path="/"
                element={
                  <MarketingPage
                    cart={cart}
                    addToCart={addToCart}
                    removeFromCart={removeFromCart}
                    updateQuantity={updateQuantity}
                    clearCart={clearCart}
                  />
                }
              >
                <Route index element={<ProductListing addToCart={addToCart} />} />
                <Route path="linen-collection" element={<LinenLanding addToCart={addToCart} />} />
                <Route path="product/:id" element={<ProductPage addToCart={addToCart} />} />
              </Route>
            </Routes>
          </Suspense>
          <WhatsAppFab />
        </Router>
      </AppTheme>
    </ErrorBoundary>
  );
}

export default App;
